import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { CarIcon } from '@/components/icons/CarIcon'

const PHOTO_BUCKET = 'employee-photos'
const VEHICLE_PHOTO_BUCKET = 'vehicle-photos'

interface InstructorInfo {
  fullName: string | null
  email: string | null
  phone: string | null
  photoPath: string | null
}

interface VehicleInfo {
  make: string
  model: string
  year: number | null
  colour: string | null
  licencePlate: string | null
  vin: string | null
  transmission: 'automatic' | 'manual' | null
  photoPath: string | null
}

const sectionStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid rgba(27,42,74,0.08)',
  borderRadius: 14,
  padding: '22px 22px',
  marginBottom: 18,
}

const sectionTitleStyle: React.CSSProperties = {
  font: '600 15px Inter, sans-serif',
  color: '#1B2A4A',
  marginBottom: 16,
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  font: '500 12.5px Inter, sans-serif',
  color: 'rgba(27,42,74,0.6)',
  marginBottom: 6,
}

export default function MyInstructor() {
  const { profile, profileLoading } = useAuth()

  const [instructor, setInstructor] = useState<InstructorInfo | null | undefined>(undefined)
  const [vehicle, setVehicle] = useState<VehicleInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!profile?.id) return
    let cancelled = false

    async function fetchInstructor() {
      setLoading(true)
      setError('')

      // RLS (migration 027) lets a student read their active instructor's profiles row;
      // "instructor_profiles: student read own instructor" (migration 104) adds the same
      // for instructor_profiles (photo_path here).
      const { data: relData, error: relError } = await supabase
        .from('instructor_student_relationships')
        .select(`
          instructor_id,
          instructor:profiles!instructor_id ( full_name, email, phone )
        `)
        .eq('student_id', profile!.id)
        .eq('status', 'active')
        .maybeSingle()

      if (cancelled) return

      if (relError) {
        setError('Unable to load instructor information.')
        setInstructor(null)
        setLoading(false)
        return
      }
      if (!relData) {
        setInstructor(null)
        setLoading(false)
        return
      }

      const row = relData as unknown as {
        instructor_id: string
        instructor: { full_name: string | null; email: string | null; phone: string | null } | null
      }

      const { data: profData } = await supabase
        .from('instructor_profiles')
        .select('photo_path')
        .eq('user_id', row.instructor_id)
        .maybeSingle()

      if (cancelled) return

      setInstructor({
        fullName: row.instructor?.full_name ?? null,
        email: row.instructor?.email ?? null,
        phone: row.instructor?.phone ?? null,
        photoPath: profData?.photo_path ?? null,
      })

      // Try school vehicle first (migration 104), then instructor's own vehicle (migration 106)
      // RLS ("school_vehicles: student read own instructor's assigned vehicle") already
      // scopes an unfiltered select to just this instructor's current assignment.
      const { data: schoolVehicleData } = await supabase
        .from('school_vehicles')
        .select('make, model, year, colour, licence_plate, vin, transmission, photo_path')
        .limit(1)
        .maybeSingle()

      if (cancelled) return

      if (schoolVehicleData) {
        setVehicle({
          make: schoolVehicleData.make,
          model: schoolVehicleData.model,
          year: schoolVehicleData.year,
          colour: schoolVehicleData.colour,
          licencePlate: schoolVehicleData.licence_plate,
          vin: schoolVehicleData.vin,
          transmission: schoolVehicleData.transmission,
          photoPath: schoolVehicleData.photo_path,
        })
      } else {
        // No school vehicle, try instructor's own vehicle (migration 106)
        // RLS ("instructor_vehicles: student read own instructor's vehicle") lets the student
        // read it via is_active_instructors_own_vehicle()
        const { data: instructorVehicleData } = await supabase
          .from('instructor_vehicles')
          .select('make, model, year, colour, licence_plate, vin, transmission, photo_path')
          .limit(1)
          .maybeSingle()

        if (cancelled) return

        setVehicle(instructorVehicleData ? {
          make: instructorVehicleData.make,
          model: instructorVehicleData.model,
          year: instructorVehicleData.year,
          colour: instructorVehicleData.colour,
          licencePlate: instructorVehicleData.licence_plate,
          vin: instructorVehicleData.vin,
          transmission: instructorVehicleData.transmission,
          photoPath: instructorVehicleData.photo_path,
        } : null)
      }

      setLoading(false)
    }

    fetchInstructor()
    return () => { cancelled = true }
  }, [profile?.id])

  if (profileLoading || loading) {
    return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>
  }

  if (error) {
    return <div className='banner-error'>{error}</div>
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>My Instructor</div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 20 }}>
        Your current driving instructor's contact details and assigned vehicle
      </div>

      {!instructor ? (
        <div style={sectionStyle}>
          <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            You don't have an active instructor right now.
          </div>
        </div>
      ) : (
        <>
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 20 }}>
              <div style={{
                width: 84, height: 84, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                background: 'rgba(27,42,74,0.06)', border: '1px solid rgba(27,42,74,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {instructor.photoPath
                  ? <img
                      src={supabase.storage.from(PHOTO_BUCKET).getPublicUrl(instructor.photoPath).data.publicUrl}
                      alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  : <span style={{ font: '600 28px Inter, sans-serif', color: 'rgba(27,42,74,0.3)' }}>
                      {(instructor.fullName || '?').trim().charAt(0).toUpperCase()}
                    </span>}
              </div>
              <div style={{ font: '600 18px Inter, sans-serif', color: '#1B2A4A' }}>
                {instructor.fullName ?? 'Your instructor'}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Email</label>
                {instructor.email ? (
                  <a href={`mailto:${instructor.email}`} style={{
                    display: 'block', font: '400 14px Inter, sans-serif', color: '#0F7C6C',
                    padding: '11px 13px', background: 'rgba(27,42,74,0.04)', borderRadius: 9, textDecoration: 'none',
                  }}>
                    {instructor.email}
                  </a>
                ) : (
                  <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '11px 13px', background: 'rgba(27,42,74,0.04)', borderRadius: 9 }}>—</div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                {instructor.phone ? (
                  <a href={`tel:${instructor.phone}`} style={{
                    display: 'block', font: '400 14px Inter, sans-serif', color: '#0F7C6C',
                    padding: '11px 13px', background: 'rgba(27,42,74,0.04)', borderRadius: 9, textDecoration: 'none',
                  }}>
                    {instructor.phone}
                  </a>
                ) : (
                  <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '11px 13px', background: 'rgba(27,42,74,0.04)', borderRadius: 9 }}>—</div>
                )}
              </div>
            </div>
          </div>

          {vehicle && (
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Vehicle</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 64, height: 64, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                  background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {vehicle.photoPath ? (
                    <img
                      src={supabase.storage.from(VEHICLE_PHOTO_BUCKET).getPublicUrl(vehicle.photoPath).data.publicUrl}
                      alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <CarIcon style={{ width: 32, height: 32, color: 'rgba(27,42,74,0.25)' }} />
                  )}
                </div>
                <div>
                  <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>
                    {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                  </div>
                  <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 2 }}>
                    {[vehicle.colour, vehicle.transmission, vehicle.licencePlate].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
